import { useActionViewerScope } from "@client/actions/useAction";
import { APP_SHELL_CLASSNAME } from "@client/appShellClassName";
import { DevDebugPanel } from "@client/components/utils/DevDebugPanel";
import { OutOfDateVersionWarning } from "@client/components/utils/OutOfDateVersionWarning";
import { SearchShortcut } from "@client/components/utils/SearchShortcut";
import { useEffectiveUser } from "@client/hooks/use-effective-user";
import { ImpersonateProvider } from "@client/ImpersonateContext";
import { ThemeProvider, Toaster, TooltipProvider } from "@sfc/ui";
import type { ReactNode } from "react";

const toasterStyle = {
  zIndex: 10_001 /* modal uses 10_000 */,
  pointerEvents: "auto",
} as const;

/**
 * The chrome every web app entry (S-Process, Spec Grants, Ops) renders inside:
 * theme, tooltips, the viewport-locked shell, View-As, toasts, the new-version
 * prompt and the dev Debug panel. Each app brings its own router, auth gate,
 * app bar and routes (wrapped in `RouteBoundary`).
 */
export function WebAppShell({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider>
      <TooltipProvider delay={0}>
        <div id="app" className={APP_SHELL_CLASSNAME}>
          <OutOfDateVersionWarning />
          <SearchShortcut />
          <ImpersonateProvider>
            <ActionViewerScope />
            {children}
            <Toaster style={toasterStyle} />
          </ImpersonateProvider>
          <DevDebugPanel />
        </div>
      </TooltipProvider>
    </ThemeProvider>
  );
}

// Scope in-flight action state to whoever is being viewed AS. Impersonation
// is app-scoped and changes request identity with no reload, so without this
// one viewer's pending work — and its owned poll, now authenticated as
// somebody else — would survive into the next viewer's session. A null leaf
// so the user query doesn't re-render the whole shell.
export function ActionViewerScope() {
  useActionViewerScope(useEffectiveUser().data?.userId ?? null);

  return null;
}
